import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { LayoutDashboard } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Breadcrumbs } from "../components/layout/Breadcrumbs";
import { PageTitle } from "../components/layout/PageTitle";
import { StatCard } from "../components/StatCard";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ErrorState } from "../components/ErrorState";
import { EmptyState } from "../components/EmptyState";
import { useAuth } from "../hooks/useAuth";
import { getGlobalStats } from "../services/stats.service";
import { listEvents } from "../services/event.service";
import { getApiErrorMessage } from "../lib/apiClient";
import { EventStatusBadge } from "../components/EventStatusBadge";

function SuperAdminOverview(): JSX.Element {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["stats", "global"], queryFn: getGlobalStats });

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorState message={getApiErrorMessage(error)} />;
  if (!data) return <EmptyState title="No data available" />;

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatCard label="Active Courses" value={data.courseCount} />
      <StatCard label="Open/Draft Events" value={data.activeEventCount} hint={`${data.totalEvents} total events`} />
      <StatCard label="Parents" value={data.parentCount} />
      <StatCard label="Students" value={data.studentCount} />
      <StatCard label="Staff Users" value={data.userCount} />
      <StatCard label="Total Enrolments" value={data.totalEnrolments} />
      <StatCard label="Confirmed" value={data.confirmed} tone="green" />
      <StatCard label="Confirmation Rate" value={`${data.confirmationRate}%`} tone="blue" />
    </div>
  );
}

function EventListOverview(): JSX.Element {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["events"], queryFn: listEvents });

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorState message={getApiErrorMessage(error)} />;
  if (!data || data.length === 0) {
    return <EmptyState title="No events assigned" description="You have not been assigned to any events yet." />;
  }

  return (
    <div className="card divide-y divide-slate-100 dark:divide-slate-700">
      {data.map((event) => (
        <Link key={event.id} to={`/events/${event.id}`} className="flex items-center justify-between gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors duration-150">
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-100">{event.name}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {new Date(event.eventDate).toLocaleDateString()} - {event.location}
            </p>
          </div>
          <EventStatusBadge status={event.status} />
        </Link>
      ))}
    </div>
  );
}

export default function DashboardOverviewPage(): JSX.Element {
  const { user } = useAuth();

  return (
    <DashboardLayout>
      <Breadcrumbs items={[{ label: "Overview" }]} />
      <PageTitle
        icon={LayoutDashboard}
        title={`Welcome${user ? `, ${user.name.split(" ")[0]}` : ""}`}
        description="Here's what's happening across your events."
      />

      <div className="mt-6">
        {user?.role === "SUPER_ADMIN" ? <SuperAdminOverview /> : <EventListOverview />}
      </div>
    </DashboardLayout>
  );
}
